import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import {
    Card,
    CardContent,
} from "@/components/ui/Card"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/Table"
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/Select"
import {
    PD_SCORE_OPTIONS,
    BI_SCORE_OPTIONS,
    LIKELIHOOD_OPTIONS,
    calculateDerivedValues,
    getPdScoreColor,
    getBiScoreColor,
} from "@/services/inherentRiskService";

const generateRowId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID()
    }
    return `row-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function isInteractiveScrollTarget(target) {
    if (!(target instanceof Element)) return false
    // Custom Select uses divs for items; pointer capture on the table would steal events.
    if (target.closest("[data-select-root]")) return true
    return Boolean(
        target.closest(
            'button, input, textarea, select, option, a[href], [role="combobox"], [role="listbox"], [role="option"], [data-radix-popper-content-wrapper], [data-state="open"]'
        )
    )
}

/**
 * Horizontal scroll synced across header, spacer strip (visible scrollbar under thead), and body.
 * Header/body use hidden horizontal bars so the red bar sits directly under the column headers.
 * Mouse / pen: click-drag to pan horizontally (touch keeps native behavior).
 */
function SyncedHorizontalScroll({ layoutKey, header, body }) {
    const headerRef = useRef(null)
    const midRef = useRef(null)
    const bodyRef = useRef(null)
    const [mirrorWidth, setMirrorWidth] = useState(0)
    const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false)
    const [isDragPanning, setIsDragPanning] = useState(false)
    const dragRef = useRef(null)

    const refreshMirrorWidth = useCallback(() => {
        const h = headerRef.current
        const b = bodyRef.current
        const hScroll = h?.scrollWidth ?? 0
        const hClient = h?.clientWidth ?? 0
        const bScroll = b?.scrollWidth ?? 0
        const bClient = b?.clientWidth ?? 0
        const w = Math.max(hScroll, bScroll)
        if (w > 0) setMirrorWidth(w)
        const maxScrollable = Math.max(hScroll - hClient, bScroll - bClient, 0)
        // Avoid showing custom scrollbar due to tiny subpixel rounding differences.
        setHasHorizontalOverflow(maxScrollable > 8)
    }, [])

    useLayoutEffect(() => {
        refreshMirrorWidth()
        const observers = []
        for (const root of [headerRef.current, bodyRef.current]) {
            if (!root) continue
            const ro = new ResizeObserver(refreshMirrorWidth)
            ro.observe(root)
            const table = root.querySelector("table")
            if (table) ro.observe(table)
            observers.push(ro)
        }
        return () => observers.forEach((ro) => ro.disconnect())
    }, [layoutKey, refreshMirrorWidth])

    const maxScrollLeft = useCallback(() => {
        const h = headerRef.current
        const m = midRef.current
        const b = bodyRef.current
        return Math.max(
            (h?.scrollWidth ?? 0) - (h?.clientWidth ?? 0),
            (m?.scrollWidth ?? 0) - (m?.clientWidth ?? 0),
            (b?.scrollWidth ?? 0) - (b?.clientWidth ?? 0),
            0
        )
    }, [])

    const setAllScrollLeft = useCallback((left) => {
        const max = maxScrollLeft()
        const clamped = Math.max(0, Math.min(max, left))
        const h = headerRef.current
        const m = midRef.current
        const b = bodyRef.current
        if (h) h.scrollLeft = clamped
        if (m) m.scrollLeft = clamped
        if (b) b.scrollLeft = clamped
    }, [maxScrollLeft])

    const syncFrom = useCallback((source) => {
        const h = headerRef.current
        const m = midRef.current
        const b = bodyRef.current
        if (!m) return
        let left
        if (source === "header") {
            if (!h) return
            left = h.scrollLeft
        } else if (source === "mid") {
            left = m.scrollLeft
        } else {
            if (!b) return
            left = b.scrollLeft
        }
        if (h && h.scrollLeft !== left) h.scrollLeft = left
        if (m.scrollLeft !== left) m.scrollLeft = left
        if (b && b.scrollLeft !== left) b.scrollLeft = left
    }, [])

    const endDragPan = useCallback((el, pointerId) => {
        dragRef.current = null
        setIsDragPanning(false)
        if (el && pointerId != null) {
            try {
                el.releasePointerCapture(pointerId)
            } catch {
                /* not captured */
            }
        }
    }, [])

    const onPointerDown = useCallback(
        (e) => {
            if (e.pointerType === "touch") return
            if (e.button !== 0) return
            if (isInteractiveScrollTarget(e.target)) return
            const el = e.currentTarget
            if (el.scrollWidth <= el.clientWidth + 1) return
            dragRef.current = {
                pointerId: e.pointerId,
                startX: e.clientX,
                startY: e.clientY,
                startScroll: el.scrollLeft,
                locked: false,
                el,
            }
            el.setPointerCapture(e.pointerId)
        },
        []
    )

    const onPointerMove = useCallback(
        (e) => {
            const d = dragRef.current
            if (!d || e.pointerId !== d.pointerId) return
            const dx = e.clientX - d.startX
            const dy = e.clientY - d.startY
            if (!d.locked) {
                if (Math.hypot(dx, dy) < 6) return
                if (Math.abs(dy) >= Math.abs(dx)) {
                    endDragPan(d.el, d.pointerId)
                    return
                }
                d.locked = true
                setIsDragPanning(true)
            }
            e.preventDefault()
            setAllScrollLeft(d.startScroll - dx)
        },
        [endDragPan, setAllScrollLeft]
    )

    const onPointerUp = useCallback(
        (e) => {
            const d = dragRef.current
            if (!d || e.pointerId !== d.pointerId) return
            endDragPan(d.el, d.pointerId)
        },
        [endDragPan]
    )

    const onLostPointerCapture = useCallback(() => {
        const d = dragRef.current
        if (d) endDragPan(null, null)
    }, [endDragPan])

    const grabCursor = cn(isDragPanning ? "cursor-grabbing" : "cursor-grab")

    const xScroll = "overflow-x-auto overscroll-x-contain touch-pan-x [scrollbar-gutter:stable]"
    const midStrip = cn(
        xScroll,
        "custom-scrollbar shrink-0 overflow-y-hidden min-h-[14px] max-h-[18px]",
        grabCursor
    )

    return (
        <div className={cn("flex w-full min-w-0 flex-col", isDragPanning && "select-none")}>
            <div
                ref={headerRef}
                onScroll={() => syncFrom("header")}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onLostPointerCapture={onLostPointerCapture}
                className={cn(
                    xScroll,
                    "no-scrollbar sticky top-[48px] z-20 shrink-0 overflow-y-hidden bg-navy-600",
                    grabCursor
                )}
            >
                {header}
            </div>
            <div
                ref={midRef}
                onScroll={() => syncFrom("mid")}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onLostPointerCapture={onLostPointerCapture}
                className={cn(midStrip, !hasHorizontalOverflow && "hidden")}
            >
                <div
                    aria-hidden
                    className="h-2.5 shrink-0"
                    style={{ width: mirrorWidth > 0 ? mirrorWidth : "100%" }}
                />
            </div>
            <div
                ref={bodyRef}
                onScroll={() => syncFrom("body")}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onLostPointerCapture={onLostPointerCapture}
                className={cn(xScroll, "no-scrollbar overflow-y-visible", grabCursor)}
            >
                {body}
            </div>
        </div>
    )
}

const createEmptyRow = (overrides = {}) => ({
    id: overrides.id ?? generateRowId(),
    persistedId: overrides.persistedId ?? null,
    risk: overrides.risk ?? "",
    category: overrides.category ?? "",
    pd: overrides.pd ?? "",
    bi: overrides.bi ?? "",
    pdBi: overrides.pdBi ?? "",
    consequence: overrides.consequence ?? "",
    likelihood: overrides.likelihood ?? "",
    inherentRisk: overrides.inherentRisk ?? "",
    score: overrides.score ?? "",
})

const InherentRiskTab = forwardRef(
    (
        {
            rows,
            setRows,
            onRowRemoved = () => { },
            onRiskCategoryChange = () => { },
            onDerivedValuesChange = () => { },
        },
        ref
    ) => {
        const riskRows = rows || [];
        const [enteringRowIds, setEnteringRowIds] = useState([]);
        const [removingRowIds, setRemovingRowIds] = useState([]);
        const [isMobile, setIsMobile] = useState(false);

        useEffect(() => {
            const updateIsMobile = () => {
                setIsMobile(window.innerWidth < 768)
            }
            updateIsMobile()
            window.addEventListener("resize", updateIsMobile)
            return () => window.removeEventListener("resize", updateIsMobile)
        }, [])

        const handleRowChange = (rowId, field, value) => {
            let derivedValues = null

            setRows((prev) => {
                const updated = prev.map((row) => {
                    if (row.id === rowId) {
                        const updatedRow = { ...row, [field]: value }
                        if (field === 'pd' || field === 'bi' || field === 'likelihood') {
                            const derived = calculateDerivedValues(updatedRow)
                            derivedValues = derived
                            return { ...updatedRow, ...derived }
                        }
                        return updatedRow
                    }
                    return row
                })

                return updated
            })

            if (field === "risk" || field === "category") {
                onRiskCategoryChange(rowId, { [field]: value }, "inherent")
            }

            if (derivedValues) {
                onDerivedValuesChange(rowId, derivedValues)
            }
        }

        const getData = () => {
            return riskRows;
        }

        useImperativeHandle(ref, () => ({
            getData,
        }))

        const columns = [
            { key: "risk", label: "Risk Section", type: "text", minWidth: 220 },
            { key: "category", label: "Risk Sub-Section", type: "text", minWidth: 220 },
            { key: "pd", label: "PD Score", type: "select", minWidth: 120 },
            { key: "bi", label: "BI Score", type: "select", minWidth: 120 },
            { key: "pdBi", label: "PD*BI", type: "readonly", minWidth: 100 },
            { key: "consequence", label: "Consequence", type: "readonly", minWidth: 140 },
            { key: "likelihood", label: "Likelihood", type: "select", minWidth: 140 },
            { key: "inherentRisk", label: "Inherent Risk", type: "readonly", minWidth: 140 },
            { key: "score", label: "Inherent Risk Score", type: "readonly", minWidth: 120 },
        ]

        const stickyColumns = isMobile ? columns.slice(0, 1) : columns.slice(0, 2)
        const stickyOffsets = stickyColumns.reduce((acc, column, index) => {
            if (index === 0) {
                acc[column.key] = 0
                return acc
            }
            const previousWidth = stickyColumns
                .slice(0, index)
                .reduce((sum, item) => sum + item.minWidth, 0)
            acc[column.key] = previousWidth
            return acc
        }, {})

        const createHeadClassNames = (key) =>
            cn(
                "p-4 text-xs font-semibold tracking-wide uppercase text-gray-200 whitespace-nowrap bg-transparent overflow-hidden",
                key === "score" && "whitespace-normal break-words leading-tight",
                key in stickyOffsets && "sticky z-20 bg-navy-600"
            )

        const createCellClassNames = (key) =>
            cn(
                "p-2 text-sm text-gray-0 last:border-b-0",
                key in stickyOffsets && "sticky z-10 bg-navy-600"
            )

        const headClassNames = createHeadClassNames
        const cellClassNames = createCellClassNames

        const columnStyle = (column) => ({
            width: column.minWidth,
            minWidth: column.minWidth,
            maxWidth: column.minWidth,
            ...(column.key in stickyOffsets ? { left: `${stickyOffsets[column.key]}px` } : {}),
        })
        const totalTableWidth = columns.reduce((sum, column) => sum + column.minWidth, 0)
        const getColWidthStyle = (column) => ({
            width: column.minWidth,
            minWidth: column.minWidth,
            maxWidth: column.minWidth,
        })

        const renderCellContent = (column, row) => {
            if (column.type === "select") {
                if (column.key === "pd") {
                    return (
                        <Select
                            value={row.pd}
                            onValueChange={(value) => handleRowChange(row.id, "pd", value)}
                        >
                            <SelectTrigger className="w-full h-12">
                                <SelectValue placeholder="Select PD">
                                    {row.pd && (
                                        <div className="flex items-center gap-2">
                                            <div className={cn("w-3 h-3 rounded-full", getPdScoreColor(parseFloat(row.pd)))} />
                                            <span>{row.pd}</span>
                                        </div>
                                    )}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="bg-navy-100 text-gray-0">
                                <SelectItem
                                    value=""
                                    className="border-b border-gray-600/30 text-gray-400 hover:text-gray-0"
                                >
                                    Clear
                                </SelectItem>
                                {PD_SCORE_OPTIONS.map((option) => (
                                    <SelectItem key={option} value={option.toString()}>
                                        <div className="flex items-center gap-2">
                                            <div className={cn("w-3 h-3 rounded-full", getPdScoreColor(option))} />
                                            <span>{option}</span>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    );
                } else if (column.key === "bi") {
                    return (
                        <Select
                            value={row.bi}
                            onValueChange={(value) => handleRowChange(row.id, "bi", value)}
                        >
                            <SelectTrigger className="w-full h-12">
                                <SelectValue placeholder="Select BI">
                                    {row.bi && (
                                        <div className="flex items-center gap-2">
                                            <div className={cn("w-3 h-3 rounded-full", getBiScoreColor(row.bi))} />
                                            <span>{row.bi}</span>
                                        </div>
                                    )}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="bg-navy-100 text-gray-0">
                                <SelectItem
                                    value=""
                                    className="border-b border-gray-600/30 text-gray-400 hover:text-gray-0"
                                >
                                    Clear
                                </SelectItem>
                                {BI_SCORE_OPTIONS.map((option) => (
                                    <SelectItem key={option} value={option.toString()}>
                                        <div className="flex items-center gap-2">
                                            <div className={cn("w-3 h-3 rounded-full", getBiScoreColor(option))} />
                                            <span>{option}</span>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    );
                } else if (column.key === "likelihood") {
                    return (
                        <Select
                            value={row.likelihood}
                            onValueChange={(value) => handleRowChange(row.id, "likelihood", value)}
                        >
                            <SelectTrigger className="w-full h-12">
                                <SelectValue placeholder="Select Likelihood">
                                    {row.likelihood && <span>{row.likelihood}</span>}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="bg-navy-100 text-gray-0">
                                <SelectItem
                                    value=""
                                    className="border-b border-gray-600/30 text-gray-400 hover:text-gray-0"
                                >
                                    Clear
                                </SelectItem>
                                {LIKELIHOOD_OPTIONS.map((option) => (
                                    <SelectItem key={option} value={option}>
                                        <span>{option}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    );
                }
            } else if (column.type === "readonly") {
                let displayValue = row[column.key];
                let colorClass = "";

                if (column.key === "consequence" && row.consequence) {
                    displayValue = <span>{row.consequence}</span>;
                } else if (column.key === "inherentRisk" && row.inherentRisk) {
                    displayValue = <span>{row.inherentRisk}</span>;
                } else if (column.key === "score" && row.score) {
                    displayValue = <span>{row.score}</span>;
                }

                return (
                    <div className="flex items-center h-12 px-3 rounded-t-lg bg-navy-100 text-gray-0">
                        {displayValue || <span className="text-gray-400">-</span>}
                    </div>
                );
            } else {
                // Risk and Category are now read-only
                if (column.key === "risk" || column.key === "category") {
                    return (
                        <div className="flex items-center h-12 px-3 rounded-t-lg bg-navy-100 text-gray-0">
                            {row[column.key] || <span className="text-gray-400">-</span>}
                        </div>
                    );
                }
                return (
                    <Input
                        type={column.type}
                        value={row[column.key]}
                        onChange={(event) =>
                            handleRowChange(
                                row.id,
                                column.key,
                                event.target.value
                            )
                        }
                        className="w-full h-12"
                    />
                );
            }
        }

        return (
            <Card className="pt-4 border-none slide-up">
                <CardContent className="p-0">
                    <div className="space-y-4">
                        <div className="relative">
                            {riskRows.length === 0 ? (
                                <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-gray-400 border border-gray-600 border-dashed rounded-lg">
                                    <p className="text-base font-semibold text-gray-0">No inherent risk rows yet</p>
                                    <p className="text-sm text-gray-400">First, add a risk and category to the mine type. <a href="/mine-types" className="text-red-500 capitalize">Click here</a> to add a risk and category.</p>
                                </div>
                            ) : (
                                <SyncedHorizontalScroll
                                    layoutKey={riskRows.map((r) => r.id).join(",")}
                                    header={
                                        <Table
                                            className="table-fixed"
                                            style={{ width: totalTableWidth, minWidth: totalTableWidth }}
                                            containerClassName="overflow-visible"
                                        >
                                            <colgroup>
                                                {columns.map((column) => (
                                                    <col key={column.key} style={getColWidthStyle(column)} />
                                                ))}
                                            </colgroup>
                                            <TableHeader className="text-left">
                                                <TableRow>
                                                    {columns.map((column) => (
                                                        <TableHead
                                                            key={column.key}
                                                            className={headClassNames(column.key)}
                                                            style={columnStyle(column)}
                                                        >
                                                            {column.label}
                                                        </TableHead>
                                                    ))}
                                                </TableRow>
                                            </TableHeader>
                                        </Table>
                                    }
                                    body={
                                        <Table
                                            className="table-fixed"
                                            style={{ width: totalTableWidth, minWidth: totalTableWidth }}
                                            containerClassName="overflow-visible"
                                        >
                                            <colgroup>
                                                {columns.map((column) => (
                                                    <col key={column.key} style={getColWidthStyle(column)} />
                                                ))}
                                            </colgroup>
                                            <TableBody>
                                                {riskRows.map((row) => {
                                                    const rowClassName = removingRowIds.includes(row.id)
                                                        ? "row-exit"
                                                        : enteringRowIds.includes(row.id)
                                                            ? "row-enter"
                                                            : ""
                                                    return (
                                                        <React.Fragment key={`risk-row-${row.id}`}>
                                                            <TableRow className={rowClassName}>
                                                                {columns.map((column) => (
                                                                    <TableCell
                                                                        key={column.key}
                                                                        className={cellClassNames(column.key)}
                                                                        style={columnStyle(column)}
                                                                    >
                                                                        {renderCellContent(column, row)}
                                                                    </TableCell>
                                                                ))}
                                                            </TableRow>
                                                        </React.Fragment>
                                                    )
                                                })}
                                            </TableBody>
                                        </Table>
                                    }
                                />
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        )
    })

InherentRiskTab.displayName = "InherentRiskTab"

export default InherentRiskTab;
export { createEmptyRow };

